/**
 * S5 Research / backtest — setup, report and trade list. Backlog tasks 70 and 71.
 *
 * `UI-Design-Spec.md#s5--research--backtest` gives the report's layout in priority order and
 * says the order is deliberate. It is followed here top to bottom:
 *
 * 1. **Net return is the hero.** Gross and fees sit beside it, smaller, fees in `--loss`.
 *    *"The size difference is the argument"* — per the turnover budget, gross return is close
 *    to meaningless, and a layout setting both at one size says otherwise.
 * 2. **The compact metric grid**, deliberately tight *"so it reads as one block rather than as
 *    twelve findings"*.
 * 3. **The equity curve**, with the buy-and-hold benchmark on it by default rather than behind
 *    a toggle — `Backtesting-and-Simulation.md` rule 3.
 * 4. **Health warnings above the trade list**, as attention cards. *"This is the section most
 *    likely to be skipped and the section most likely to be right, so it gets position
 *    priority over the data it summarises."*
 * 5. **The trade list** (task 71), sortable, with the per-trade chart on selection.
 *
 * ## The cost line is never folded into net return
 *
 * *"The cost line is always shown, never folded into net return: costs as a share of gross
 * profit, with its verdict."* It has its own tile, and on a sub-1h run it is usually the whole
 * story.
 *
 * ## What is absent, and why each one is absent rather than stubbed
 *
 * - **Progress over WebSocket.** The spec wants bars processed, elapsed and an ETA streamed.
 *   The `job:{id}` topic exists in the socket vocabulary and answers `unavailable` — there is
 *   no producer. So a running backtest is *polled*, which the app already does for the session
 *   and for `/health/deep`, and the screen says "running" rather than inventing a percentage.
 * - **The underwater plot, monthly heatmap and trade distribution.** Each needs a series the
 *   API does not return: the equity curve is served, its drawdown decomposition is not.
 *   `EquityPlot` draws the drawdown shading it can compute from the curve itself.
 *
 * ## Three of those absences are now filled, and one is not
 *
 * - **Sweep mode** is `SweepReport` — the `CautionBand` the spec makes undismissable, the
 *   configurations-tried denominator, the heatmap, and G4's per-parameter profile. It renders when
 *   the selected run's kind is `sweep`.
 * - **The promotion gate** is `GatePanel`: G1-G12 with a verdict word per row, and the holdout it
 *   opens. `unknown` gets its own count, because *"no walk-forward run exists"* and *"the folds
 *   were inconsistent"* do not have the same fix.
 * - **The benchmark line** is drawn. `backtest_runs.benchmark_curve` (migration `0048`) is the
 *   column that was missing; the tile stays, because a return and a line answer different
 *   questions.
 * - **Progress over WebSocket** is still absent and still polled, for the reason above: the
 *   `job:{id}` topic has no producer. A running sweep now reports configurations executed against
 *   configurations tried, which is real progress from a real column rather than an invented
 *   percentage.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { api } from '../api/client';
import type {
  RunResponse,
  StrategyResponse,
  SweepResponse,
  TradeResponse,
} from '../api/generated';
import { TradeChart } from '../charts/TradeChart';
import { AttentionCard, AttentionPanel } from '../design/components/messages/AttentionCard.jsx';
import { Button } from '../design/components/controls/Button.jsx';
import { DataTable } from '../design/components/data/DataTable.jsx';
import { EquityPlot } from '../design/components/data/EquityPlot.jsx';
import { EmptyState } from '../design/components/messages/EmptyState.jsx';
import { ErrorPanel } from '../design/components/messages/ErrorPanel.jsx';
import { FormField } from '../design/components/controls/FormField.jsx';
import { MetricTile } from '../design/components/data/MetricTile.jsx';
import { Num } from '../design/components/data/Num.jsx';
import { SegmentedControl } from '../design/components/controls/SegmentedControl.jsx';
import { Select } from '../design/components/controls/Select.jsx';
import { Skeleton } from '../design/components/messages/Skeleton.jsx';
import { GatePanel } from '../research/GatePanel';
import { SweepReport } from '../research/SweepReport';
import { fetchBenchmarkCurve, fetchEquityCurve, type CurvePoint } from '../research/curve';
import {
  DIRECTION_LABEL,
  type Direction,
  directionCounts,
  sortRows,
  strategyRows,
} from '../research/strategyRows';
import { formatDecimal, formatPrice, toPlot } from '../money';

/** How often a `queued` or `running` row is re-read. See the module docstring on progress. */
const POLL_MS = 2000;

/** States that will not change again, so polling stops. */
const TERMINAL = new Set(['done', 'failed', 'cancelled']);

/**
 * `Timeframe`'s own order, shortest first.
 *
 * ⚠ Sorting the rungs alphabetically puts `1d` before `1h` before `4h`, which reads as a sequence
 * and is not one. Anything absent sorts last rather than first, so a rung this list has not heard
 * of appears at the end instead of silently leading.
 */
const RUNG_ORDER = ['1m', '5m', '15m', '1h', '4h', '1d', '1w'];

type Metrics = Record<string, string | number | boolean | null>;

/**
 * A signed figure that keeps its digits and gains its colour.
 *
 * `Num` colours profit and loss only for a **numeric** value — `colour && signed && numeric` —
 * and every money value here arrives as a string, because `money.ts`'s rule is that *"a price
 * stays a `string` from the wire to the pixel"*. Passing a `number` to get the colour would
 * convert a decimal to an IEEE double on the way to the screen, which is the H6 breach that rule
 * exists to prevent, on the screen an operator reads a result off.
 *
 * So the string is kept and the colour comes from `Num`'s own `style` prop, using the design
 * system's own tokens. No new colour is invented and no digit is lost.
 */
function Signed({
  value,
  prefix = '',
  suffix = '',
  step = 'md',
}: {
  value: string | null;
  prefix?: string;
  suffix?: string;
  step?: 'display' | 'xl' | 'lg' | 'md' | 'sm' | 'xs';
}): React.JSX.Element {
  if (value === null) return <Num value={null} step={step} />;
  const negative = value.trim().startsWith('-');
  const magnitude = formatDecimal(negative ? value.trim().slice(1) : value, 2);
  return (
    <Num
      value={`${negative ? '\u2212' : '+'}${prefix}${magnitude}${suffix}`}
      step={step}
      style={{ color: negative ? 'var(--loss)' : 'var(--profit)' }}
    />
  );
}

function metric(metrics: Metrics | null | undefined, key: string): string | null {
  const value = metrics?.[key];
  return value === null || value === undefined ? null : String(value);
}

/** A percentage as the report prints it: two decimals, never scientific. */
function pct(value: string | null): string {
  return value === null ? '\u2014' : `${formatDecimal(value, 2)}%`;
}

/**
 * A ratio at two decimals.
 *
 * **Found by looking at it.** `Num` returns a string untouched — that is the H6-safe path
 * `money.ts` relies on — so a Sharpe handed over verbatim rendered as
 * `-3.403306263991847261839803819` and overran the tile beside it. Every ratio in `Metrics`
 * comes out of a `Decimal` division, which carries 28 significant digits by default. Two is
 * what a Sharpe means; the rest is arithmetic exhaust.
 */
function ratio(value: string | null): string {
  return value === null ? '\u2014' : formatDecimal(value, 2);
}

/** `1 run`, `2 runs`. Read out loud before shipping; "1 runs" is what a count without one says. */
function plural(n: number, noun: string): string {
  return `${n} ${noun}${n === 1 ? '' : 's'}`;
}

export function ResearchScreen(): React.JSX.Element {
  const [params, setParams] = useSearchParams();
  const client = useQueryClient();
  const selectedRunId = params.get('run');
  /*
    🔴 **The filters live in the URL beside the run, not in component state.** Three rungs times
    thirteen strategies is thirty-nine walk-forwards in one dropdown, and finding *"TrendFollower
    at 1h"* by scrolling is the problem this screen now has. Putting the filter in the query string
    means a narrowed view is a link — which is how an operator actually returns to a comparison
    they were part-way through, and how one gets sent to a future session.
  */
  const strategyFilter = params.get('strategy') ?? '';
  const timeframeFilter = params.get('tf') ?? '';
  const [selectedTrade, setSelectedTrade] = useState<number | null>(null);

  const strategies = useQuery({
    queryKey: ['strategies'],
    queryFn: () => api.research.listStrategies({}),
  });

  const runs = useQuery({
    queryKey: ['runs'],
    queryFn: () => api.research.listRuns({}),
    // Any run in flight keeps the list moving; a settled list stops asking.
    refetchInterval: (query) => {
      const list = query.state.data?.runs ?? [];
      return list.some((run: RunResponse) => !TERMINAL.has(run.state)) ? POLL_MS : false;
    },
  });

  const allRuns: RunResponse[] = runs.data?.runs ?? [];
  /*
    🔴 **Each filter's own options are computed WITHOUT itself applied**, and getting that wrong is
    a one-way door. The first version built the strategy list from the fully-filtered runs — so
    once `TrendFollower` was chosen, the only strategy with any runs was `TrendFollower`, and the
    dropdown offered exactly it. Switching to `DipBuyer` meant going back through "All strategies"
    first. A filter you cannot change without clearing it is a filter that traps you.

    So `byTimeframe` drives the strategy dropdown and its counts, and `runList` — narrowed by both
    — drives only the run selector. The rungs come from `allRuns` for the same reason: switching
    rung must always be possible.
  */
  const byTimeframe = allRuns.filter(
    (item) => timeframeFilter === '' || item.timeframe === timeframeFilter,
  );
  const runList = byTimeframe.filter(
    (item) => strategyFilter === '' || String(item.strategy_id) === strategyFilter,
  );
  /*
    🔴 **A filter that excludes the open run switches to one it includes.**

    The other way round was tried first — keep the selection so the report never changes underneath
    the reader — and it is wrong here. Choosing `1h` while reading a `4h` run then left the rung
    control saying `1h` over a `4h` report, which is *"the dropdown does not work"* a third time:
    a control that visibly changes nothing. Picking a rung means "show me that rung".

    The report does change underfoot, and that is what was asked for. `selectedRunId` is only
    honoured while it is still in view.
  */
  const run =
    runList.find((item) => String(item.id) === selectedRunId) ?? runList[0] ?? null;

  /*
    The one merge rule, shared by the filters above and the table below.

    Merge rather than replace, so changing the rung keeps the strategy filter and vice versa. An
    empty value removes its key, so a bare URL stays bare.

    ⚠ **And `run` is dropped when the new filter excludes it**, rather than left behind pointing at
    something no longer on screen. Without this the URL read `?run=19&tf=1h` while the report showed
    run #100 — deterministic, because 19 is filtered out either way, but a link that names a run it
    does not display is a link nobody can trust.

    🔴 **It is one function because two copies would drift.** The table selects a run and the
    filters narrow the set; if only one of them dropped an excluded `run`, the URL and the report
    would disagree in exactly the case this rule exists for. The table is handed `runList`, which is
    already filtered, so every row it offers survives `stillVisible`.
  */
  const update = (next: Record<string, string>): void => {
    const merged: Record<string, string> = {
      ...(run === null ? {} : { run: String(run.id) }),
      ...(strategyFilter === '' ? {} : { strategy: strategyFilter }),
      ...(timeframeFilter === '' ? {} : { tf: timeframeFilter }),
      ...next,
    };
    const stillVisible = allRuns.some(
      (item) =>
        String(item.id) === merged.run &&
        (!merged.strategy || String(item.strategy_id) === merged.strategy) &&
        (!merged.tf || item.timeframe === merged.tf),
    );
    if (!stillVisible) delete merged.run;
    setParams(Object.fromEntries(Object.entries(merged).filter(([, v]) => v !== '')));
  };

  // Selecting a different run must not leave the previous run's trade highlighted.
  useEffect(() => {
    setSelectedTrade(null);
  }, [run?.id]);

  const trades = useQuery({
    queryKey: ['run-trades', run?.id],
    queryFn: () => api.research.listTrades({ runId: run!.id }),
    enabled: run !== null && run.state === 'done',
  });

  const curve = useQuery({
    queryKey: ['run-curve', run?.id],
    queryFn: () => fetchEquityCurve(run!.id),
    enabled: run !== null && run.state === 'done',
  });

  const control = useQuery({
    queryKey: ['run-benchmark-curve', run?.id],
    queryFn: () => fetchBenchmarkCurve(run!.id),
    enabled: run !== null && run.state === 'done',
  });

  // Only for a sweep. The endpoint 409s on any other kind rather than returning an empty search,
  // so asking for one would be asking a question the run cannot answer.
  const sweep = useQuery({
    queryKey: ['run-sweep', run?.id],
    queryFn: () => api.research.getSweep({ runId: run!.id }),
    enabled: run !== null && run.kind === 'sweep',
  });

  const cancel = useMutation({
    mutationFn: (runId: number) => api.research.cancelRun({ runId }),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['runs'] }),
  });

  if (strategies.isLoading || runs.isLoading) {
    return (
      <div className="jmt-screen">
        <Skeleton count={8} />
      </div>
    );
  }
  if (runs.isError) {
    return (
      <div className="jmt-screen">
        <ErrorPanel
          message="Could not load backtests."
          detail={String((runs.error as Error)?.message ?? '')}
          onRetry={() => void runs.refetch()}
        />
      </div>
    );
  }

  return (
    <div className="jmt-screen jmt-report">
      <Setup
        strategies={strategies.data?.strategies ?? []}
        runs={runList}
        inScope={byTimeframe}
        selectedRunId={run === null ? null : String(run.id)}
        strategyFilter={strategyFilter}
        timeframeFilter={timeframeFilter}
        timeframes={[...new Set(allRuns.map((item) => item.timeframe))]}
        onChange={update}
      />
      <StrategyTable
        runs={runList}
        strategies={strategies.data?.strategies ?? []}
        selectedRunId={run === null ? null : String(run.id)}
        onSelectRun={(id) => update({ run: id })}
      />

      {run === null ? (
        <div className="jmt-note">
          <EmptyState>
            No backtests yet. A run is created through <code>POST /backtests</code> and executed by
            the <code>jmt-research</code> worker; this screen is its report.
          </EmptyState>
        </div>
      ) : (
        <Report
          run={run}
          trades={trades.data?.trades ?? []}
          curve={curve.data ?? null}
          control={control.data ?? null}
          curveError={curve.isError}
          sweep={sweep.data ?? null}
          selectedTrade={selectedTrade}
          onSelectTrade={setSelectedTrade}
          onCancel={() => cancel.mutate(run.id)}
          cancelling={cancel.isPending}
        />
      )}
    </div>
  );
}

// --- setup ------------------------------------------------------------------------

/**
 * The setup panel, reduced to choosing which run to read.
 *
 * The spec's panel *creates* a run: strategy and version, date range with presets that
 * deliberately include known-hard regimes, pairs, starting capital, fee model, slippage model.
 * **Three of those six are not the client's to choose today.** Starting capital is one number
 * for the deployment (`research.starting_capital`) rather than a field per run, because at this
 * venue what a strategy can trade at all is dominated by the capital and a run at a capital the
 * operator does not have is a fantasy with a real-looking number; the fee and slippage models
 * come from the same place. A form offering all six would imply the three are per-run.
 *
 * So this selects an existing run, and creating one stays with the API until the fields it
 * would offer are genuinely per-run. Stated rather than left as a gap.
 */
function Setup({
  strategies,
  runs,
  inScope,
  selectedRunId,
  strategyFilter,
  timeframeFilter,
  timeframes,
  onChange,
}: {
  strategies: StrategyResponse[];
  runs: RunResponse[];
  //: Every run at the chosen rung, whatever its strategy — what the strategy dropdown offers.
  inScope: RunResponse[];
  selectedRunId: string | null;
  strategyFilter: string;
  timeframeFilter: string;
  timeframes: string[];
  onChange: (next: Record<string, string>) => void;
}): React.JSX.Element {
  const names = useMemo(
    () => new Map(strategies.map((item) => [item.id, item.name])),
    [strategies],
  );
  const runsByStrategy = useMemo(() => {
    const counted = new Map<number, number>();
    for (const run of inScope) counted.set(run.strategy_id, (counted.get(run.strategy_id) ?? 0) + 1);
    return counted;
  }, [inScope]);

  /*
    🔴 **An option is ONE LINE, and everything variable-length is out of it.**

    Two reports, and the second was caused by the fix for the first.

    *"The dropdown does not work, every strategy gives the same numbers."* — it was working. Six
    runs existed, all of one strategy version, and a `full` and a `walkforward` over the same range
    **have the same headline by construction**: the walk-forward adds folds, it does not replay
    differently. So the fix was to say **what kind of run this is** and **what it produced**.

    *"The ui still is broken"* — and it was, worse. `Select`'s menu rows are `height: 32` **fixed
    and inline**, so a label that wraps does not make its row taller: it overflows into the row
    below and the two render on top of each other. My longer label wrapped at any realistic width,
    and `CrossSectionalMomentum`'s ten symbols wrapped the *trigger* to five lines. The vendored
    component is checksummed and must not be edited, so the option has to fit.

    So an option now carries only what cannot grow — `#id · kind · StrategyName` — with the outcome
    in `hint`. **Symbols, timeframe and range are deliberately not here**: they are variable-length,
    they are already on the hero tile for the selected run, and they were the whole cause. `app.css`
    then forces single-line rows with an ellipsis as a backstop, because a strategy name is
    user-supplied and the next long one must degrade rather than collide.
  */
  /*
    ⚠ **Counted from `inScope`, not from `runs`, and hidden while a strategy is chosen.** `runs` is
    narrowed by the strategy filter, so "every backtest here belongs to one strategy" was
    tautologically true the moment one was picked — a note that fires because of the control you
    just used says nothing. From `inScope` it is a real statement about the chosen rung.
  */
  const distinct = new Set(inScope.map((run) => run.strategy_id)).size;
  const unrun = strategies.length - distinct;
  const selected = runs.find((run) => String(run.id) === selectedRunId) ?? null;

  /*
    ⚠ **The rungs come from the data, not from a constant.** `Timeframe` models six and this venue
    has bars for three; a hardcoded list would offer `15m` with nothing behind it, and would miss
    the day a fourth is backfilled.
  */
  const rungs = [...timeframes].sort(
    (a, b) => RUNG_ORDER.indexOf(a) - RUNG_ORDER.indexOf(b),
  );

  return (
    <section className="jmt-report-setup">
      <div className="jmt-report-filters">
        <FormField label="Strategy">
          <Select
            width="100%"
            value={strategyFilter}
            onChange={(next: string) => onChange({ strategy: next })}
            options={[
              // ⚠ The header counts the same set the rows do. It read 29 while every row read 1,
              // because one was the unfiltered total and the others were not.
              { value: '', label: `All strategies (${plural(inScope.length, 'run')})` },
              // Only strategies that HAVE a run. Offering one with none is a filter that can
              // only produce an empty list, which reads as a broken control.
              ...strategies
                .filter((item) => runsByStrategy.has(item.id))
                .map((item) => ({
                  value: String(item.id),
                  label: item.name,
                  hint: plural(runsByStrategy.get(item.id) ?? 0, 'run'),
                })),
            ]}
          />
        </FormField>
        {rungs.length > 1 ? (
          <FormField label="Timeframe">
            <SegmentedControl
              value={timeframeFilter}
              onChange={(next: string) => onChange({ tf: next })}
              options={[{ value: '', label: 'All' }, ...rungs.map((tf) => ({ value: tf, label: tf }))]}
            />
          </FormField>
        ) : null}
      </div>

      <FormField label="Backtest">
        <Select
          width="100%"
          value={selectedRunId ?? ''}
          onChange={(next: string) => onChange({ run: next })}
          options={runs.map((run) => ({
            value: String(run.id),
            // 🔴 The rung is in the label because three runs of one strategy now differ **only**
            // by it. Still one line — see the note above on why an option may not wrap.
            label: `#${run.id} · ${run.kind} · ${
              names.get(run.strategy_id) ?? 'strategy ' + run.strategy_id
            } · ${run.timeframe}`,
            hint: outcome(run),
          }))}
        />
      </FormField>
      {runs.length === 0 ? (
        <p className="jmt-note-text">
          No run matches this filter. The report below is still the last one you opened.
        </p>
      ) : null}
      {selected === null ? null : (
        // What the option no longer carries, for the run that is actually selected — where it has
        // a whole line to itself and may wrap without landing on anything.
        <p className="jmt-note-text jmt-report-scope">
          {describeUniverse(selected)} · {selected.timeframe} ·{' '}
          {selected.range_start.slice(0, 10)} → {selected.range_end.slice(0, 10)}
        </p>
      )}
      {inScope.length > 0 && distinct === 1 && strategyFilter === '' ? (
        <p className="jmt-note-text">
          Every backtest here belongs to <strong>one strategy</strong>
          {unrun > 0 ? `, and ${unrun} of your ${strategies.length} strategies have never been ` +
            'backtested at all' : ''}
          . Two runs of the same version over the same range share a headline by construction — a{' '}
          <code>walkforward</code> replays exactly what <code>full</code> does and adds folds to it,
          and a <code>sweep</code>&rsquo;s headline is its baseline rather than its winner. What
          differs between them is further down the page.
        </p>
      ) : null}
      <p className="jmt-note-text">
        Starting capital, the fee model and the slippage model are the deployment&rsquo;s
        (<code>config/jmt-research.yaml</code>), not this form&rsquo;s: at this venue what a
        strategy can trade at all is dominated by the capital, and a run at a capital nobody has
        is a fantasy with a real-looking number.
      </p>
    </section>
  );
}

/**
 * Every strategy's headline result, sortable, in one table.
 *
 * **Why this exists.** The run selector answers *"show me this run"*. It cannot answer *"which of
 * these actually earned"*, which is the question a screen full of candidates is for — and thirteen
 * families became twenty-one on 2026-08-24. A list you scroll is not a comparison.
 *
 * 🔴 **Concentration is a column, not a footnote.** Measured across the whole batch: return and
 * top-five-trade share move together, and the two biggest earners took **93%** and **100%** of
 * their profit from a single walk-forward fold. Sorting on return alone ranks exactly the wrong way
 * round, so the number that says whether a return is *real* sits beside it and turns red past the
 * ceiling G11 refuses.
 *
 * ⚠ **It shows the runs the filters already allow**, so every row is selectable. A table offering a
 * row the surrounding filters exclude would have its selection silently dropped by `update`'s
 * `stillVisible` rule, which is worse than not offering it.
 *
 * ⚠ **Direction is local state, not a URL key** — it narrows this table rather than the report,
 * the same call `TradeList` makes for its outcome filter.
 */
function StrategyTable({
  runs,
  strategies,
  selectedRunId,
  onSelectRun,
}: {
  runs: RunResponse[];
  strategies: StrategyResponse[];
  selectedRunId: string | null;
  onSelectRun: (id: string) => void;
}): React.JSX.Element {
  const [sortKey, setSortKey] = useState('ret');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [way, setWay] = useState('');

  const latest = useMemo(() => strategyRows(runs, strategies), [runs, strategies]);
  const counts = useMemo(() => directionCounts(latest), [latest]);
  const rows = useMemo(
    () => sortRows(latest, way, sortKey, sortDir),
    [latest, way, sortKey, sortDir],
  );

  return (
    <section className="jmt-report-table">
      <h2 className="jmt-section-title">Strategies</h2>
      <div className="jmt-report-filters">
        <FormField label="Direction">
          <SegmentedControl
            value={way}
            onChange={setWay}
            options={[
              { value: '', label: `All (${latest.length})` },
              // Only the directions actually present. Offering "short only (0)" is a control that
              // can only produce an empty table.
              ...(['both', 'long', 'short', 'untagged'] as readonly Direction[])
                .filter((key) => (counts.get(key) ?? 0) > 0)
                .map((key) => ({
                  value: key,
                  label: `${DIRECTION_LABEL[key]} (${counts.get(key) ?? 0})`,
                })),
            ]}
          />
        </FormField>
      </div>
      <DataTable
        density="compact"
        stickyFirst
        sortKey={sortKey}
        sortDir={sortDir}
        onSort={(key: string) => {
          if (key === sortKey) {
            setSortDir(sortDir === 'desc' ? 'asc' : 'desc');
            return;
          }
          setSortKey(key);
          // A new numeric column opens at its interesting end. Ascending on return would open on
          // the worst result, which is not what anyone clicked for.
          setSortDir(key === 'name' || key === 'way' || key === 'rung' ? 'asc' : 'desc');
        }}
        selectedId={selectedRunId === null ? undefined : Number(selectedRunId)}
        onSelectRow={(row) => onSelectRun(String(row.id))}
        empty="No completed in-sample run matches these filters."
        /*
          ⚠ **Return and concentration sit against the name because of the phone.** The first
          column is sticky and the rest scroll; at 390px only ~198px shows beside it, so a column
          past x=500 is one nobody scrolls to. Ordered Rung → Symbols → Trades → Return, the one
          number the table exists to show needed 344px of sideways scrolling to reach. Rung and
          symbol count are context and can be the ones you scroll for.

          🔴 **Top-5 stays adjacent to Return, at every width.** Its whole job is to say whether
          the number to its left is a method or five lucky trades; a reader who sees one without
          the other is reading half the result.
        */
        columns={[
          { key: 'name', label: 'Strategy', sortable: true },
          {
            key: 'ret',
            label: 'Return (%)',
            align: 'right',
            sortable: true,
            render: (row) => <Signed value={row.retRaw} suffix="%" step="sm" />,
          },
          {
            key: 'top5',
            label: 'Top-5 (%)',
            align: 'right',
            sortable: true,
            // ⚠ Over 50% is what G11 refuses: the profit came from five trades rather than from a
            // method. Marked here because a reader sorting on return needs to see it in the same
            // glance, not after opening the report.
            render: (row) => (
              <Num
                value={pct(row.top5Raw)}
                step="sm"
                style={row.top5 > 50 ? { color: 'var(--loss)' } : undefined}
              />
            ),
          },
          {
            key: 'way',
            label: 'Direction',
            sortable: true,
            // DataTable hands rows back untyped, so the direction is narrowed here rather than
            // trusted. An unknown value falls through to itself rather than rendering blank.
            render: (row) => DIRECTION_LABEL[row.way as Direction] ?? String(row.way),
          },
          {
            key: 'sharpe',
            label: 'Sharpe',
            align: 'right',
            sortable: true,
            render: (row) => <Num value={ratio(row.sharpeRaw)} step="sm" />,
          },
          {
            key: 'dd',
            label: 'Max DD (%)',
            align: 'right',
            render: (row) => <Num value={pct(row.dd)} step="sm" />,
          },
          { key: 'trades', label: 'Trades', align: 'right', sortable: true },
          { key: 'rung', label: 'Rung', sortable: true },
          { key: 'symbols', label: 'Symbols', align: 'right', sortable: true },
        ]}
        rows={rows}
      />
    </section>
  );
}

/**
 * What a run produced, for the dropdown's hint column.
 *
 * ⚠ **Deliberately terse.** The hint shares a 32px row with the label and neither may wrap, so
 * `38t` rather than `38 trades`. Absence has its own mark.
 */
function outcome(run: RunResponse): string {
  if (run.state !== 'done') return run.state;
  const metrics = (run.metrics ?? null) as Metrics | null;
  const net = metric(metrics, 'total_return_pct');
  if (net === null) return '\u2014';
  return `${formatDecimal(net, 2)}% · ${metric(metrics, 'trade_count') ?? 0}t`;
}

/**
 * A run's universe, short enough to read.
 *
 * Ten symbols spelled out is what wrapped the select trigger to five lines, and the same list
 * under the hero tile is no more readable for being complete. Three or fewer are named; beyond
 * that the count is the fact, because *which* ten is a question the trade list answers.
 */
function describeUniverse(run: RunResponse): string {
  return run.symbols.length <= 3
    ? run.symbols.join(', ')
    : `${run.symbols.length} symbols (${run.symbols[0]}, …)`;
}

// --- report -----------------------------------------------------------------------

function Report({
  run,
  trades,
  curve,
  control,
  curveError,
  sweep,
  selectedTrade,
  onSelectTrade,
  onCancel,
  cancelling,
}: {
  run: RunResponse;
  trades: TradeResponse[];
  curve: CurvePoint[] | null;
  control: CurvePoint[] | null;
  curveError: boolean;
  sweep: SweepResponse | null;
  selectedTrade: number | null;
  onSelectTrade: (id: number | null) => void;
  onCancel: () => void;
  cancelling: boolean;
}): React.JSX.Element {
  const metrics = (run.metrics ?? null) as Metrics | null;

  if (run.state !== 'done') {
    return <NotFinished run={run} sweep={sweep} onCancel={onCancel} cancelling={cancelling} />;
  }

  const netPct = metric(metrics, 'total_return_pct');
  const grossPnl = metric(metrics, 'gross_pnl');
  const fees = metric(metrics, 'fees');
  const costShare = metric(metrics, 'cost_share_of_gross_profit_pct');
  /*
    How much of the headline is a position nobody closed. `gross_pnl` now includes it — before the
    audit it did not, and `buy_and_hold` rendered "Gross €0.00 / Fees −€0.00 / Net +€436.08" right
    here, in the three tiles the spec puts side by side so they can be read against each other.
    The number is correct now; this line is what stops it being read as a banked return.
  */
  const unrealised = metric(metrics, 'unrealised_pnl');
  const isUnrealised = unrealised !== null && Number(unrealised) !== 0;
  const benchmarkPct = run.benchmark
    ? String((run.benchmark as Record<string, unknown>).benchmark_return_pct ?? '')
    : null;
  /*
    🔴 **Which control, read from the run rather than assumed.** The tile said "buy and hold" for
    every strategy — and after D73 there are three controls. `TrendFollowerShort` is measured
    against **sell-and-hold** and a market-neutral strategy against **zero**, so the label was a
    false claim on exactly the strategies where the comparison matters most. `health.py` renamed
    its own check for this reason (*"that name was about to become a false claim"*) and the screen
    kept the old assumption.

    `_benchmark` records the control it used in the check's `limit` field, precisely so a reader a
    month later can tell. This reads it back.
  */
  const controlName = ((run.warnings ?? []) as Array<Record<string, string | undefined>>)
    .find((w) => w.name === 'beats_the_passive_control_in_this_window')
    ?.limit?.replace(/^above /, '');

  return (
    <>
      <section className="jmt-report-hero">
        <MetricTile
          variant="hero"
          label="Net return"
          value={<Signed value={netPct} suffix="%" step="display" />}
          /*
            ⚠ **The range is deliberately not repeated here.** It is on the scope line under the
            selector, which wraps; a tile's context does not — it ellipsises — so at 320px the
            full string truncated to `10 symbols (BTC/EUR, …) · 4h · 2024-09-0…` and hid the very
            thing it was there to state. Two places saying it, one of them badly, is worse than
            one saying it well.
          */
          context={`${describeUniverse(run)} · ${run.timeframe}`}
        />
        {/*
          Gross and fees are `default` variant beside a `hero` deliberately. The spec: "the size
          difference is the argument" — gross return is close to meaningless against the turnover
          budget, and a layout that set both at one size would say otherwise.
        */}
        <MetricTile
          label="Gross P&L"
          value={<Signed value={grossPnl} prefix="€" />}
          context={
            isUnrealised
              ? `includes €${formatDecimal(unrealised, 2)} still open`
              : 'all positions closed'
          }
        />
        <MetricTile
          label="Fees"
          value={
            <Num value={`\u2212€${formatDecimal(fees ?? '0', 2)}`} style={{ color: 'var(--loss)' }} />
          }
          context={costShare === null ? 'no gross profit to share' : `${pct(costShare)} of gross`}
        />
      </section>

      <section className="jmt-report-grid">
        <MetricTile label="Sharpe" value={<Num value={ratio(metric(metrics, 'sharpe'))} />} />
        <MetricTile label="Sortino" value={<Num value={ratio(metric(metrics, 'sortino'))} />} />
        <MetricTile label="Calmar" value={<Num value={ratio(metric(metrics, 'calmar'))} />} />
        <MetricTile
          label="Max drawdown"
          value={
            <Num
              value={pct(metric(metrics, 'max_drawdown_pct'))}
              style={{ color: 'var(--loss)' }}
            />
          }
          context={
            metric(metrics, 'max_drawdown_recovered') === 'True' ||
            metric(metrics, 'max_drawdown_recovered') === 'true'
              ? 'recovered'
              : 'never recovered'
          }
        />
        <MetricTile
          label="Trades"
          value={<Num value={metric(metrics, 'trade_count') ?? '0'} />}
          // "2 won in a row, 18 lost" read as a tally of the 161 trades. It is the longest
          // *streak* of each, which is a different claim entirely.
          context={`longest streak ${metric(metrics, 'longest_win_streak') ?? 0}W / ${metric(metrics, 'longest_loss_streak') ?? 0}L`}
        />
        <MetricTile label="Win rate" value={<Num value={pct(metric(metrics, 'win_rate_pct'))} />} />
        <MetricTile
          label="Profit factor"
          value={<Num value={ratio(metric(metrics, 'profit_factor'))} />}
        />
        <MetricTile
          label="Expectancy"
          value={<Signed value={metric(metrics, 'expectancy')} prefix="€" />}
          context="per trade"
        />
        <MetricTile
          label="Longest underwater"
          value={<Num value={formatDecimal(metric(metrics, 'longest_underwater_days') ?? '0', 0)} />}
          context="days"
        />
        <MetricTile
          label="Time in market"
          value={<Num value={pct(metric(metrics, 'time_in_market_pct'))} />}
        />
        <MetricTile
          label="Benchmark"
          value={<Signed value={benchmarkPct} suffix="%" />}
          /*
            ⚠ **The control's name only.** A tile's context line does not wrap — measured at 474px,
            *"buy-and-hold, from this strategy's first tradeable bar"* needed 283px in a 211px cell
            and spilled. The caveat it carried is real and belongs under the grid, where it has a
            line to itself; see the note below.
          */
          context={controlName ?? 'passive control'}
        />
        <MetricTile
          label="Rejected signals"
          value={<Num value={String(run.rejections ?? 0)} />}
          context={`of ${run.intents ?? 0} intents`}
        />
      </section>

      {/*
        🔴 **The benchmark is not comparable across strategies, and only this line says so.** The
        control enters at the strategy's *first tradeable bar*, so warm-up shifts it: measured on
        the box over one market and one two-year window, 2 bars of warm-up gives +23.93% and 210
        gives +11.70%. Monotone in warm-up. It is the right control for *this* strategy and the
        wrong number to rank two of them by, and an operator comparing the tile across runs would
        have no way to know.
      */}
      <p className="jmt-note-text">
        The benchmark is the {controlName ?? 'passive control'} entered at this strategy&rsquo;s
        first tradeable bar, so a long warm-up buys in later and returns less. It is the right
        comparison for this run and <strong>not</strong> comparable with another strategy&rsquo;s.
      </p>

      <section className="jmt-report-chart">
        <h2 className="jmt-section-title">Equity</h2>
        {curveError ? (
          <ErrorPanel
            message="Could not load the equity curve for this run."
            detail="The run finished but stored no curve, which is a defect in the worker rather than in this screen."
          />
        ) : curve === null ? (
          <Skeleton count={3} />
        ) : (
          <EquityPlot
            data={curve.map((point) => toPlot(point.equity))}
            /*
              🔴 `null` when the run predates migration `0048`, and never `[]`. An empty series
              would draw a flat control at zero, which is a specific and wrong claim about the
              market rather than the absence of one.
            */
            benchmark={control === null ? null : control.map((point) => toPlot(point.equity))}
            drawdown
            height={220}
            style={{ width: '100%' }}
          />
        )}
        <p className="jmt-note-text">
          {control === null
            ? 'This run stored no control curve — it predates the column — so the benchmark is the ' +
              'figure in the grid above and nothing is drawn for it. A flat line at zero would be a ' +
              'claim about the market rather than the absence of one.'
            : 'The dashed line is the direction-matched passive control, marked on the same bars. ' +
              'It is on the chart by default rather than behind a toggle: “returned 40% against ' +
              '120%” is only half the comparison if the 120% came through a 70% fall, and the tile ' +
              'above cannot show where the control was while this strategy drew down.'}
        </p>
      </section>

      <Folds run={run} />

      {sweep === null ? null : <SweepReport sweep={sweep} />}

      <Warnings run={run} />

      <GatePanel strategyVersionId={run.strategy_version_id} timeframe={run.timeframe} />

      <TradeList
        trades={trades}
        timeframe={run.timeframe}
        selectedTrade={selectedTrade}
        onSelectTrade={onSelectTrade}
      />

      <Reproduction run={run} />
    </>
  );
}

/** A run that has not produced a report, and what the operator can do about it. */
function NotFinished({
  run,
  sweep,
  onCancel,
  cancelling,
}: {
  run: RunResponse;
  sweep: SweepResponse | null;
  onCancel: () => void;
  cancelling: boolean;
}): React.JSX.Element {
  if (run.state === 'failed') {
    return (
      <ErrorPanel
        message={`Backtest #${run.id} failed.`}
        // The server's own sentence, rendered verbatim. The worker writes a reason naming what
        // it refused and why; adding to it here would be inventing.
        detail={run.error ?? 'No reason was recorded, which is itself a defect.'}
      />
    );
  }
  return (
    <div className="jmt-note">
      <EmptyState>
        Backtest #{run.id} is {run.state}.{' '}
        {run.state === 'cancelled'
          ? 'It was cancelled before it finished, so there is no result to show.'
          : sweep !== null
            ? `${sweep.configurations_executed} of ${sweep.configurations_tried} configurations ` +
              'have been executed and committed. Each one is a full event-driven replay, so a large ' +
              'grid is measured in hours — and a cancel keeps everything already committed.'
            : 'Progress is not streamed — the job topic has no producer yet — so this polls the run ' +
              'row every two seconds rather than showing a percentage it would have to invent.'}
      </EmptyState>
      {run.state !== 'cancelled' ? (
        <Button variant="danger" onClick={onCancel} disabled={cancelling}>
          {cancelling ? 'Cancelling…' : 'Cancel this run'}
        </Button>
      ) : null}
    </div>
  );
}

/**
 * The walk-forward fold breakdown. Task 82 stored it; nothing rendered it until now.
 *
 * 🔴 *"Fold results are reported separately. Consistency across folds is the signal; an average
 * hides the one lucky window that carried the result."* The run has carried `metrics.walkforward`
 * since task 82 and this screen showed none of it — so a `walkforward` run and a `full` run over
 * the same range were **visually identical**, which is half of why the dropdown read as broken.
 *
 * ⚠ **`optimised` is stated on every fold set**, because *"does this parameter set hold up across
 * unseen windows?"* and *"would a process that refits on history have worked?"* are different
 * questions with the same picture, and only this flag separates them.
 */
function Folds({ run }: { run: RunResponse }): React.JSX.Element | null {
  const section = ((run.metrics ?? {}) as Record<string, unknown>).walkforward as
    | Record<string, unknown>
    | undefined;
  if (section === undefined) return null;

  const folds = (section.folds ?? []) as Array<Record<string, unknown>>;
  const reason = section.reason as string | undefined;
  const optimised = section.optimised === true;
  const fitted = (section.fitted ?? []) as Array<Record<string, unknown>>;
  const combined = (section.combined ?? {}) as Metrics;

  return (
    <section className="jmt-report-folds">
      <h2 className="jmt-section-title">
        Walk-forward — {String(section.mode ?? 'anchored')},{' '}
        {optimised ? 'fitted per fold' : 'not optimised'}
      </h2>

      {folds.length === 0 ? (
        <EmptyState>
          {reason ??
            'This run produced no folds, and recorded no reason — which is itself a defect.'}
        </EmptyState>
      ) : (
        <>
          <div className="jmt-report-grid">
            <MetricTile
              label="Folds positive"
              value={<Num value={`${section.folds_positive ?? 0} of ${folds.length}`} />}
              context={`${pct(metric(section as Metrics, 'g3_consistency_pct'))} — G3 wants 60%`}
            />
            <MetricTile
              label="Largest fold"
              value={<Num value={pct(metric(section as Metrics, 'largest_fold_share_pct'))} />}
              context="of total profit — G3 wants under 50%"
            />
            <MetricTile
              label="Combined out-of-sample"
              value={<Signed value={metric(combined, 'total_return_pct')} suffix="%" />}
              context={`${metric(combined, 'trade_count') ?? 0} trades across the test windows`}
            />
            <MetricTile
              label="Control, same windows"
              value={<Signed value={metric(section as Metrics, 'control_return_pct')} suffix="%" />}
              context="what G8 is measured against"
            />
          </div>

          <DataTable
            density="compact"
            columns={[
              { key: 'fold', label: 'Fold' },
              { key: 'test', label: 'Test window' },
              { key: 'ret', label: 'Return (%)', align: 'right' },
              { key: 'trades', label: 'Trades', align: 'right' },
              { key: 'chose', label: 'Fitted to' },
            ]}
            rows={folds.map((fold, index) => {
              const metrics = (fold.metrics ?? {}) as Metrics;
              const chosen = fitted.find((entry) => entry.index === fold.index);
              return {
                id: String(fold.index ?? index),
                fold: `#${Number(fold.index ?? index) + 1}`,
                test: `${String(fold.test_start ?? '').slice(0, 10)} → ${String(
                  fold.test_end ?? '',
                ).slice(0, 10)}`,
                ret: pct(metric(metrics, 'total_return_pct')),
                trades: String(fold.trades ?? 0),
                // `—` when nothing was fitted. Absence has its own mark and is not a blank.
                chose:
                  chosen === undefined
                    ? '\u2014'
                    : Object.entries((chosen.parameters ?? {}) as Record<string, string>)
                        .map(([name, value]) => `${name} ${value}`)
                        .join(', '),
              };
            })}
          />
          <p className="jmt-note-text">
            {optimised
              ? 'Each fold chose its own parameters on its training window and was then measured ' +
                'on the test window alone. That answers “would a process that refits on history ' +
                'have worked?”.'
              : 'Every fold ran the version’s declared parameters. That answers “does this ' +
                'parameter set hold up across unseen windows?” — a lesser claim than fitting, and ' +
                'the one this run makes. Supply a grid to fit per fold.'}
          </p>
        </>
      )}
    </section>
  );
}

/**
 * The health warnings, above the trade list.
 *
 * *"This is the section most likely to be skipped and the section most likely to be right, so
 * it gets position priority over the data it summarises."* Every check is shown, including the
 * ones that passed and the ones that could not run — `UNKNOWN` is not a pass, and a panel that
 * showed only failures would make "nothing to report" and "nothing was checked" the same view.
 */
function Warnings({ run }: { run: RunResponse }): React.JSX.Element {
  const warnings = (run.warnings ?? []) as Array<Record<string, string | undefined>>;
  const problems = warnings.filter((w) => w.health === 'warning' || w.health === 'critical');
  const unknown = warnings.filter((w) => w.health === 'unknown');

  return (
    <section className="jmt-report-warnings">
      <h2 className="jmt-section-title">Health warnings</h2>
      <AttentionPanel
        count={problems.length}
        empty="Every realism check passed. That is not the same as a good strategy."
      >
        {problems.map((warning) => (
          <AttentionCard
            key={warning.name}
            severity={warning.health === 'critical' ? 'critical' : 'attention'}
            title={warning.message ?? warning.name ?? 'unnamed check'}
            detail={`${warning.name} · ${warning.actual} against ${warning.limit}`}
          />
        ))}
      </AttentionPanel>
      {unknown.length > 0 ? (
        <p className="jmt-note-text">
          {unknown.length} check{unknown.length === 1 ? '' : 's'} could not run:{' '}
          {unknown.map((w) => w.name ?? '?').join(', ')}. <strong>Not a pass</strong> — a check nobody ran
          did not pass, and the report says so rather than counting it clean.
        </p>
      ) : null}
    </section>
  );
}

/**
 * The trade list and the per-trade chart. Backlog task 71.
 *
 * *"Clicking a trade opens the chart at that moment with the strategy's own indicators drawn —
 * the single most useful debugging view in the whole product."* The indicators are not drawn:
 * the API stores no per-bar indicator series, and recomputing them in the browser would be a
 * second implementation of `ctx.indicators`, which is the one thing `Strategy-SDK.md` says must
 * never happen — *"a chart drawing a different RSI from the strategy is a trap"*. So the chart
 * marks the entry and the exit on the equity curve around the trade, which is what the stored
 * data supports honestly.
 */
function TradeList({
  trades,
  timeframe,
  selectedTrade,
  onSelectTrade,
}: {
  trades: TradeResponse[];
  timeframe: string;
  selectedTrade: number | null;
  onSelectTrade: (id: number | null) => void;
}): React.JSX.Element {
  const [outcome, setOutcome] = useState<'all' | 'wins' | 'losses'>('all');

  const rows = useMemo(() => {
    return trades
      .filter((trade) => {
        if (outcome === 'all') return true;
        const net = Number(trade.net_pnl ?? '0');
        return outcome === 'wins' ? net > 0 : net <= 0;
      })
      .map((trade) => ({
        id: trade.id,
        entry: trade.entry_ts.slice(0, 16).replace('T', ' '),
        side: trade.side,
        quantity: formatDecimal(trade.quantity, 5),
        entryPrice: formatPrice(trade.entry_price),
        exitPrice: trade.exit_price === null ? '—' : formatPrice(trade.exit_price),
        net: trade.net_pnl,
        fees: trade.fees,
        mae: trade.mae,
        mfe: trade.mfe,
        reason: trade.exit_reason ?? '—',
      }));
  }, [trades, outcome]);

  const selected = trades.find((trade) => trade.id === selectedTrade) ?? null;

  return (
    <section className="jmt-report-trades">
      <h2 className="jmt-section-title">Trades</h2>
      <div className="jmt-filter-row">
        {(['all', 'wins', 'losses'] as const).map((option) => (
          <Button
            key={option}
            variant={outcome === option ? 'primary' : 'ghost'}
            onClick={() => setOutcome(option)}
          >
            {option}
          </Button>
        ))}
      </div>

      {/* The table sits in a flush panel, matching S14: a lifted surface with no padding
          of its own, because the table draws its own cell padding. D66 gave the world
          depth and left this screen drawing its table straight onto the canvas. */}
      <div className="jmt-panel jmt-panel--flush">
      <DataTable
        density="compact"
        stickyFirst
        selectedId={selectedTrade ?? undefined}
        onSelectRow={(row) => onSelectTrade(Number(row.id) === selectedTrade ? null : Number(row.id))}
        empty="No trades. A backtest that placed none is a result, and usually the most informative one at small capital."
        columns={[
          { key: 'entry', label: 'Entry', mono: true },
          { key: 'side', label: 'Side' },
          { key: 'quantity', label: 'Quantity', align: 'right', mono: true },
          { key: 'entryPrice', label: 'Entry (€)', align: 'right', mono: true },
          { key: 'exitPrice', label: 'Exit (€)', align: 'right', mono: true },
          {
            key: 'net',
            label: 'Net (€)',
            align: 'right',
            render: (row) => <Signed value={row.net} prefix="€" step="sm" />,
          },
          {
            key: 'fees',
            label: 'Fees (€)',
            align: 'right',
            render: (row) => (
              <Num
                value={formatDecimal(row.fees ?? '0', 2)}
                step="sm"
                style={{ color: 'var(--loss)' }}
              />
            ),
          },
          {
            key: 'mae',
            label: 'MAE (€)',
            align: 'right',
            render: (row) => <Num value={formatDecimal(row.mae ?? '0', 2)} />,
          },
          {
            key: 'mfe',
            label: 'MFE (€)',
            align: 'right',
            render: (row) => <Num value={formatDecimal(row.mfe ?? '0', 2)} />,
          },
          { key: 'reason', label: 'Exit' },
        ]}
        rows={rows}
      />
      </div>

      {selected !== null ? <TradeChart trade={selected} timeframe={timeframe} /> : null}
    </section>
  );
}

/**
 * *"Reproduction details are attached — code hash, data range, parameters, seed, SDK version.
 * Any run can be re-executed exactly."*
 *
 * And the cost model, because *"a result produced with optimistic assumptions carries that fact
 * with it"* — which is only true if the fact is on the screen the result is read from.
 */
function Reproduction({ run }: { run: RunResponse }): React.JSX.Element {
  const costs = (run.cost_model ?? {}) as Record<string, unknown>;
  return (
    <section className="jmt-report-repro">
      <h2 className="jmt-section-title">Reproduction</h2>
      <dl className="jmt-repro-list">
        <dt>Run</dt>
        {/*
          Plain text, not a `StatePill`. A pill labelled "quick" and styled `VALIDATED` asserts
          a lifecycle state this run does not have — the kind of a backtest and the state of a
          strategy are different facts, and borrowing the component makes the screen claim one
          when it means the other.
        */}
        <dd>
          #{run.id} · {run.kind} · {run.state}
        </dd>
        <dt>Version</dt>
        <dd>strategy {run.strategy_id}, version id {run.strategy_version_id}</dd>
        <dt>Seed</dt>
        <dd>{run.seed}</dd>
        <dt>Range</dt>
        <dd>
          {run.range_start.slice(0, 10)} → {run.range_end.slice(0, 10)} ·{' '}
          {run.bars_replayed ?? '—'} bars replayed
        </dd>
        <dt>Parameters</dt>
        <dd>
          {Object.keys(run.parameters ?? {}).length === 0
            ? 'defaults'
            : JSON.stringify(run.parameters)}
        </dd>
        <dt>Cost model</dt>
        <dd>
          {Object.keys(costs).length === 0
            ? 'the worker’s configured defaults'
            : JSON.stringify(costs)}
        </dd>
      </dl>
    </section>
  );
}
